// Flag/correct modal + Add-card modal (with AI suggestion). Extracts
// openFlagModal / saveFlagModal + openAddCardModal / saveAddCardModal /
// suggestAddCardModal from legacy/app.js, plus the internal helpers
// appendLocalCard / findLocalDuplicate / pickSuggestSeed.
//
// Webhook calls are delegated via the deps (pushCardUpdate / Add /
// Suggest) so this module stays framework- and storage-agnostic.

import { DEFAULT_LEVEL, LEVELS } from "../config/defaults";
import { LANG_NAMES, LANGS, type Lang } from "../config/langs";
import type { CardList } from "../config/lists";
import { normalizeAnswer } from "../domain/text";
import { currentLocale, t } from "../i18n";
import type { Card, CefrLevel, Direction } from "../types";
import { byId, hide, show } from "./dom";
import { escapeHtml } from "./html";

type AddResult = {
  ok: boolean;
  error?: string;
  card?: Record<string, string> & { tags?: string; level?: string };
};
type UpdateResult = { ok: boolean; error?: string };
type SuggestResult =
  | {
      ok: true;
      translations: Record<string, string>;
      tags: string;
      level: string;
    }
  | { ok: false; error?: string; unsupported?: boolean };

export type CardModalsDeps = {
  getCurrent: () => Card | null;
  getCards: () => Card[];
  getUserLevel: () => CefrLevel;
  getSourceLang: () => Lang | null;
  getDirection: () => Direction;
  /** Active deck — modals iterate its faces to render the right input
   *  fields (Lang faces for bilingual, face slugs for mono). */
  getList: () => CardList;
  /** Legacy fallback: source lang derived from the UI locale. */
  knownLang: () => Lang;
  pushCardUpdate: (
    card: Card,
    newTexts: Record<string, string>,
    reason: string,
  ) => Promise<UpdateResult>;
  pushCardAdd: (
    texts: Record<string, string>,
    tags: string,
    level: string,
  ) => Promise<AddResult>;
  pushCardSuggest: (
    sourceLang: Lang,
    sourceText: string,
  ) => Promise<SuggestResult>;
  /** Re-render footer stats after a card was added/corrected. */
  renderStats: () => void;
};

export type CardModals = {
  flagCurrentCard: () => void;
  openFlagModal: (card: Card) => void;
  closeFlagModal: () => void;
  saveFlagModal: () => Promise<void>;
  openAddCardModal: () => void;
  closeAddCardModal: () => void;
  saveAddCardModal: () => Promise<void>;
  suggestAddCardModal: () => Promise<void>;
};

// Insert a freshly-added card into the in-memory deck so the user sees
// it immediately without reloading the sheet. Mirrors the id and tags
// shape produced by parseCards() — tags is an array, level defaults to
// B1 to match the parser fallback. `faceKeys` comes from the active
// deck in catalog order so the id uses the same priority the CSV
// parser would.
function appendLocalCard(
  cards: Card[],
  card: Record<string, string> & { tags?: string; level?: string },
  faceKeys: readonly string[],
  tags: string,
  level: string,
): void {
  let idKey: string | null = null;
  for (const k of faceKeys) {
    if (card[k]) {
      idKey = k;
      break;
    }
  }
  if (!idKey) return;
  const id = idKey + "::" + card[idKey];
  if (cards.some((c) => c.id === id)) return;
  const faces: Partial<Record<string, string>> = {};
  for (const k of faceKeys) {
    if (card[k]) faces[k] = card[k];
  }
  const tagArr = (tags || "")
    .split(/[,;]/)
    .map((x) => x.trim())
    .filter(Boolean);
  const upper = (level || "").toUpperCase();
  const lv: CefrLevel = (LEVELS as readonly string[]).includes(upper)
    ? (upper as CefrLevel)
    : "B1";
  cards.push({ id, faces, level: lv, tags: tagArr });
}

export function createCardModals(deps: CardModalsDeps): CardModals {
  // ----- Flag modal --------------------------------------------------

  function openFlagModal(card: Card): void {
    const loc = currentLocale();
    const list = deps.getList();
    const fields = byId("flagModalFields");
    if (fields) {
      fields.innerHTML = list.faces
        .map((face) => {
          const label = face.lang
            ? LANG_NAMES[loc][face.lang]
            : (t(face.labelKey) as string);
          const val = card.faces[face.key] || "";
          return (
            `<label class="form-label" for="flagField_${face.key}">${escapeHtml(label)}</label>` +
            `<input type="text" class="modal-input" id="flagField_${face.key}" data-face-key="${face.key}" value="${escapeHtml(val)}" />`
          );
        })
        .join("");
    }
    const reason = byId<HTMLTextAreaElement>("flagModalReason");
    if (reason) reason.value = "";
    const status = byId("flagModalStatus");
    if (status) {
      status.className = "modal-status hidden";
      status.textContent = "";
    }
    const saveBtn = byId<HTMLButtonElement>("flagModalSaveBtn");
    if (saveBtn) saveBtn.disabled = false;
    show(byId("flagModal"));
    // focus first field for quick correction
    const first = fields?.querySelector<HTMLInputElement>("input");
    if (first) setTimeout(() => first.focus(), 30);
  }

  function closeFlagModal(): void {
    hide(byId("flagModal"));
  }

  function flagCurrentCard(): void {
    const c = deps.getCurrent();
    if (!c) return;
    openFlagModal(c);
  }

  async function saveFlagModal(): Promise<void> {
    const c = deps.getCurrent();
    if (!c) {
      closeFlagModal();
      return;
    }
    const fields = byId("flagModalFields");
    const newTexts: Record<string, string> = {};
    fields
      ?.querySelectorAll<HTMLInputElement>("input[data-face-key]")
      .forEach((inp) => {
        const key = inp.dataset.faceKey;
        if (key) newTexts[key] = inp.value.trim();
      });
    const reasonEl = byId<HTMLTextAreaElement>("flagModalReason");
    const reason = reasonEl?.value.trim() || "";
    const saveBtn = byId<HTMLButtonElement>("flagModalSaveBtn");
    if (saveBtn) saveBtn.disabled = true;
    const status = byId("flagModalStatus");
    if (status) {
      status.className = "modal-status";
      status.textContent = t("flag_modal_saving") as string;
    }
    const result = await deps.pushCardUpdate(c, newTexts, reason);
    if (result.ok) {
      if (status) {
        status.className = "modal-status ok";
        status.textContent = t("flag_modal_ok") as string;
      }
      // Refresh card display so corrections are visible immediately.
      deps.renderStats();
      if (deps.getCurrent() === c) {
        const cardPrompt = byId("cardPrompt");
        if (cardPrompt) {
          cardPrompt.textContent = c.faces[deps.getDirection().from] || "";
        }
      }
      setTimeout(() => closeFlagModal(), 800);
    } else {
      if (status) {
        status.className = "modal-status err";
        status.textContent = (t("flag_modal_err") as string) + " " + result.error;
      }
      if (saveBtn) saveBtn.disabled = false;
    }
  }

  // ----- Add-card modal ---------------------------------------------

  function openAddCardModal(): void {
    const loc = currentLocale();
    const list = deps.getList();
    const isMono = list.shape === "mono";
    // One input per deck face. Labels are the localized language name
    // for bilingual faces (LANG_NAMES) or the face's i18n label for
    // mono faces.
    const fields = byId("addCardModalFields");
    if (fields) {
      fields.innerHTML = list.faces
        .map((face) => {
          const label = face.lang
            ? LANG_NAMES[loc][face.lang]
            : (t(face.labelKey) as string);
          return (
            `<label class="form-label" for="addCardField_${face.key}">${escapeHtml(label)}</label>` +
            `<input type="text" class="modal-input" id="addCardField_${face.key}" data-face-key="${face.key}" autocomplete="off" />`
          );
        })
        .join("");
    }
    const tags = byId<HTMLInputElement>("addCardModalTags");
    if (tags) tags.value = "";
    // Level (CEFR) only makes sense on bilingual language decks.
    const levelLabel = byId("addCardModalLevelLabel");
    const levelSel = byId<HTMLSelectElement>("addCardModalLevel");
    levelLabel?.classList.toggle("hidden", isMono);
    levelSel?.classList.toggle("hidden", isMono);
    if (levelSel) levelSel.value = deps.getUserLevel() || DEFAULT_LEVEL;
    const status = byId("addCardModalStatus");
    if (status) {
      status.className = "modal-status hidden";
      status.textContent = "";
    }
    const saveBtn = byId<HTMLButtonElement>("addCardModalSaveBtn");
    if (saveBtn) saveBtn.disabled = false;
    // AI suggest is a translation helper — not applicable for mono
    // decks where both faces are in the same language.
    const suggestBtn = byId<HTMLButtonElement>("addCardModalSuggestBtn");
    if (suggestBtn) {
      suggestBtn.classList.toggle("hidden", isMono);
      suggestBtn.disabled = false;
      const label = suggestBtn.querySelector("#addCardModalSuggestLabel");
      if (label) label.textContent = t("add_card_suggest") as string;
    }
    show(byId("addCardModal"));
    // Focus the preferred input: source-lang for bilingual, first face
    // for mono.
    let focusInp: HTMLInputElement | null = null;
    if (!isMono) {
      const srcLang = deps.getSourceLang() || deps.knownLang();
      focusInp = byId<HTMLInputElement>("addCardField_" + srcLang);
    }
    focusInp ??= fields?.querySelector<HTMLInputElement>("input") ?? null;
    if (focusInp) setTimeout(() => focusInp!.focus(), 30);
  }

  function closeAddCardModal(): void {
    hide(byId("addCardModal"));
  }

  // Client-side dedup: scan in-memory cards for an exact match (after
  // the same normalization the comparator uses) on any typed face.
  // Catches dupes immediately so the user doesn't even hit the webhook.
  // Server still rechecks as the source of truth.
  function findLocalDuplicate(
    texts: Record<string, string>,
  ): Card | null {
    const norm = (s: string | undefined) => normalizeAnswer(s || "");
    const faceKeys = deps.getList().faces.map((f) => f.key);
    for (const c of deps.getCards()) {
      for (const key of faceKeys) {
        const typed = (texts[key] || "").trim();
        if (!typed) continue;
        const existing = (c.faces[key] || "").trim();
        if (existing && norm(existing) === norm(typed)) {
          return c;
        }
      }
    }
    return null;
  }

  async function saveAddCardModal(): Promise<void> {
    const list = deps.getList();
    const faceKeys = list.faces.map((f) => f.key);
    const fields = byId("addCardModalFields");
    const texts: Record<string, string> = {};
    fields
      ?.querySelectorAll<HTMLInputElement>("input[data-face-key]")
      .forEach((inp) => {
        const key = inp.dataset.faceKey;
        if (key) texts[key] = inp.value.trim();
      });
    const filledFaces = faceKeys.filter((k) => texts[k]);
    const status = byId("addCardModalStatus");
    if (filledFaces.length < 2) {
      if (status) {
        status.className = "modal-status err";
        status.textContent = t("add_card_need_two") as string;
      }
      return;
    }
    const dup = findLocalDuplicate(texts);
    if (dup) {
      if (status) {
        const firstKey = filledFaces[0]!;
        status.className = "modal-status err";
        status.textContent =
          (t("add_card_dup") as string) +
          " (" +
          (dup.faces[firstKey] || dup.id) +
          ")";
      }
      return;
    }
    const tagsEl = byId<HTMLInputElement>("addCardModalTags");
    const levelSel = byId<HTMLSelectElement>("addCardModalLevel");
    const tags = tagsEl?.value.trim() || "";
    // CEFR level only applies to bilingual decks; mono decks send "".
    const level = list.shape === "bilingual" ? levelSel?.value || "" : "";
    const saveBtn = byId<HTMLButtonElement>("addCardModalSaveBtn");
    if (saveBtn) saveBtn.disabled = true;
    if (status) {
      status.className = "modal-status";
      status.textContent = t("add_card_sending") as string;
    }
    const result = await deps.pushCardAdd(texts, tags, level);
    if (result.ok) {
      if (status) {
        status.className = "modal-status ok";
        status.textContent = t("add_card_ok") as string;
      }
      if (result.card)
        appendLocalCard(deps.getCards(), result.card, faceKeys, tags, level);
      deps.renderStats();
      setTimeout(() => closeAddCardModal(), 800);
    } else {
      if (status) {
        status.className = "modal-status err";
        status.textContent = (t("add_card_err") as string) + " " + result.error;
      }
      if (saveBtn) saveBtn.disabled = false;
    }
  }

  // Pick the best seed for the AI: prefer the user's source language
  // (the one they already speak well, so most likely accurate), then
  // any non-empty field. Returns null if everything is blank.
  // AI suggest is bilingual-only — caller checks list.shape before
  // reaching this.
  function pickSuggestSeed(): { lang: Lang; text: string } | null {
    const fields = byId("addCardModalFields");
    const inputs: Partial<Record<Lang, string>> = {};
    fields
      ?.querySelectorAll<HTMLInputElement>("input[data-face-key]")
      .forEach((inp) => {
        const key = inp.dataset.faceKey;
        if (!key) return;
        // Only Lang-coded keys are meaningful for the translation
        // prompt; mono face keys (country / capital) are filtered out
        // by the (LANGS as readonly string[]).includes check below.
        if ((LANGS as readonly string[]).includes(key)) {
          inputs[key as Lang] = inp.value.trim();
        }
      });
    const preferred = deps.getSourceLang() || deps.knownLang();
    const preferredVal = inputs[preferred];
    if (preferredVal) return { lang: preferred, text: preferredVal };
    for (const lang of LANGS) {
      const v = inputs[lang];
      if (v) return { lang, text: v };
    }
    return null;
  }

  async function suggestAddCardModal(): Promise<void> {
    const seed = pickSuggestSeed();
    const status = byId("addCardModalStatus");
    if (!seed) {
      if (status) {
        status.className = "modal-status err";
        status.textContent = t("add_card_suggest_empty") as string;
      }
      return;
    }
    const suggestBtn = byId<HTMLButtonElement>("addCardModalSuggestBtn");
    const saveBtn = byId<HTMLButtonElement>("addCardModalSaveBtn");
    if (suggestBtn) suggestBtn.disabled = true;
    if (saveBtn) saveBtn.disabled = true;
    if (status) {
      status.className = "modal-status";
      status.textContent = t("add_card_suggesting") as string;
    }
    const result = await deps.pushCardSuggest(seed.lang, seed.text);
    if (suggestBtn) suggestBtn.disabled = false;
    if (saveBtn) saveBtn.disabled = false;
    if (!result.ok) {
      if (status) {
        status.className = "modal-status err";
        status.textContent = result.unsupported
          ? (t("add_card_suggest_unsupported") as string)
          : (t("add_card_suggest_err") as string) + " " + result.error;
      }
      return;
    }
    // Fill missing language fields — never overwrite user input. The
    // seed field is always preserved.
    const fields = byId("addCardModalFields");
    fields?.querySelectorAll<HTMLInputElement>("input[data-lang]").forEach((inp) => {
      const lang = inp.dataset.lang as Lang;
      if (lang === seed.lang) return;
      if (inp.value.trim()) return;
      const proposed = (result.translations && result.translations[lang]) || "";
      if (proposed) inp.value = proposed;
    });
    const tagsEl = byId<HTMLInputElement>("addCardModalTags");
    if (tagsEl && !tagsEl.value.trim() && result.tags) {
      tagsEl.value = result.tags;
    }
    // Level: always overwrite — the AI estimate is the whole point.
    const levelSel = byId<HTMLSelectElement>("addCardModalLevel");
    if (levelSel && result.level) {
      const upper = result.level.toUpperCase();
      if ((LEVELS as readonly string[]).includes(upper)) levelSel.value = upper;
    }
    if (status) {
      status.className = "modal-status hidden";
      status.textContent = "";
    }
  }

  return {
    flagCurrentCard,
    openFlagModal,
    closeFlagModal,
    saveFlagModal,
    openAddCardModal,
    closeAddCardModal,
    saveAddCardModal,
    suggestAddCardModal,
  };
}
